/**
 * Enterprise Dashboard API Service
 * Functions for fetching enterprise usage data
 */

import {
  EnterpriseDashboardData,
  CustomerDetailResponse,
  TimeRange,
} from './types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

async function fetchWithAuth<T>(url: string): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: errorData.detail || `HTTP ${response.status}: Failed to fetch`,
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('API Error:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An error occurred',
    };
  }
}

export async function fetchEnterpriseDashboard(
  timeRange: TimeRange
): Promise<ApiResponse<EnterpriseDashboardData>> {
  return fetchWithAuth<EnterpriseDashboardData>(
    `${API_URL}/admin/enterprise/dashboard?time_range=${timeRange}`
  );
}

export async function fetchEnterpriseCustomerDetail(
  customerId: number,
  timeRange: TimeRange
): Promise<ApiResponse<CustomerDetailResponse>> {
  return fetchWithAuth<CustomerDetailResponse>(
    `${API_URL}/admin/enterprise/customers/${customerId}?time_range=${timeRange}`
  );
}

export async function fetchEnterpriseFeatureRuns(
  timeRange: TimeRange,
  userId?: number
): Promise<ApiResponse<{ feature_runs: any[] }>> {
  const userParam = userId ? `&user_id=${userId}` : '';
  return fetchWithAuth(
    `${API_URL}/admin/enterprise/feature-runs?time_range=${timeRange}${userParam}`
  );
}

export async function fetchEnterpriseUsecaseBreakdown(
  timeRange: TimeRange,
  userId?: number
): Promise<ApiResponse<{ usecase_breakdown: any[] }>> {
  const userParam = userId ? `&user_id=${userId}` : '';
  return fetchWithAuth(
    `${API_URL}/admin/enterprise/usecase-breakdown?time_range=${timeRange}${userParam}`
  );
}

export async function fetchEnterpriseCustomers(
  timeRange: TimeRange
): Promise<ApiResponse<{ customers: any[] }>> {
  return fetchWithAuth(
    `${API_URL}/admin/enterprise/customers?time_range=${timeRange}`
  );
}
